import * as React from "react";

import { splitHearingTitle } from "@/lib/public-hearings/title";
import type { Hearing } from "@/lib/public-hearings/types";

type Props = {
  hearing: Hearing;
  titleId?: string;
  /** When compact, the eyebrow ledger row is the full eyebrow — the parent
   *  has its own "back to list" affordance via CloseControls so we don't
   *  duplicate it. */
  compact: boolean;
};

// Masthead: eyebrow ledger (mono "council · public hearing" kicker +
// italic Garamond item-number folio) → display headline with community
// bolded, address as italic Garamond suffix → 2px ink rule.
//
// Mirrors the .ph-day header vocabulary (folio italic + mono kicker +
// heavy display + italic suffix) so a hearing brief reads as a child of
// the day-block it was pulled out of.
export function DetailMasthead({ hearing: h, titleId, compact }: Props) {
  const { headline, address } = splitHearingTitle(h.title, h.communities);
  const section = h.sectionLabel || "Public Hearing";

  return (
    <header className="ph-detail-masthead">
      <div className="ph-detail-eyebrow-row">
        <span className="ph-detail-eyebrow">
          {compact ? null : (
            <>
              <span>Council</span>
              <span className="sep" aria-hidden>
                ·
              </span>
            </>
          )}
          <span>{section}</span>
        </span>
        {h.itemNumber && (
          <span className="ph-detail-folio" aria-label={`Item ${h.itemNumber}`}>
            <span className="pre">item</span>
            {h.itemNumber}
          </span>
        )}
      </div>

      <h1 id={titleId} className="ph-detail-head" title={h.title}>
        {headline.map((seg, i) =>
          seg.bold ? (
            <span key={i} className="community">
              {seg.text}
            </span>
          ) : (
            <React.Fragment key={i}>{seg.text}</React.Fragment>
          ),
        )}
        {address && (
          <span className="addr">
            <span className="pre">at</span>
            {address}
          </span>
        )}
      </h1>
    </header>
  );
}
